import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBusinessProfiles, type BusinessProfile } from '../../hooks/useBusinessProfiles';
import { useUserPlan } from '../../hooks/useUserPlan';

const businessTypeIcons: Record<string, string> = {
  'Plumbing / HVAC': 'ri-tools-line',
  'Landscaping': 'ri-leaf-line',
  'Cleaning Services': 'ri-home-smile-line',
  'Restaurant / Food': 'ri-restaurant-line',
  'Fitness / Gym': 'ri-heart-pulse-line',
  'Legal Services': 'ri-scales-line',
  'Real Estate': 'ri-home-8-line',
  'E-commerce / Retail': 'ri-shopping-bag-line',
  'Healthcare': 'ri-hospital-line',
  'Education / Tutoring': 'ri-graduation-cap-line',
  'Auto / Mechanic': 'ri-car-line',
  'SaaS / Software': 'ri-code-line',
  'Consulting': 'ri-briefcase-line',
  'Beauty / Salon': 'ri-scissors-line',
  'Other': 'ri-building-2-line',
};

const positioningColors: Record<string, string> = {
  affordable: 'bg-green-50 text-green-600',
  quality: 'bg-blue-50 text-blue-600',
  premium: 'bg-purple-50 text-purple-700',
  luxury: 'bg-amber-50 text-amber-600',
  innovative: 'bg-teal-50 text-teal-600',
  community: 'bg-orange-50 text-orange-600',
};

interface ProfileCardProps {
  profile: BusinessProfile;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function ProfileCard({ profile, onEdit, onDuplicate, onDelete }: ProfileCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const icon = businessTypeIcons[profile.business_type] ?? 'ri-building-2-line';
  const posColor = positioningColors[profile.brand_positioning] ?? 'bg-stone-50 text-stone-500';

  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-5 hover:border-orange-200 transition-all duration-200 group relative">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-orange-100 to-amber-50 shrink-0 overflow-hidden">
            {profile.logo_url ? (
              <img src={profile.logo_url} alt={`${profile.business_name} logo`} className="w-full h-full object-contain p-1" />
            ) : (
              <i className={`${icon} text-orange-500 text-lg`} />
            )}
          </div>
          <div>
            <h3 className="text-sm font-bold text-stone-900">{profile.business_name}</h3>
            <p className="text-xs text-stone-500">{profile.business_type}</p>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-more-2-fill text-sm" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-9 bg-white border border-stone-200 rounded-xl py-1 w-40 z-20 shadow-sm">
              <button onClick={() => { onEdit(); setMenuOpen(false); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-stone-600 hover:bg-stone-50 cursor-pointer">
                <i className="ri-edit-line text-sm" /> Edit
              </button>
              <button onClick={() => { onDuplicate(); setMenuOpen(false); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-stone-600 hover:bg-stone-50 cursor-pointer">
                <i className="ri-file-copy-line text-sm" /> Duplicate
              </button>
              <div className="border-t border-stone-100 my-1" />
              <button onClick={() => { onDelete(); setMenuOpen(false); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-500 hover:bg-red-50 cursor-pointer">
                <i className="ri-delete-bin-line text-sm" /> Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="space-y-2 mb-4">
        {profile.location && (
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <i className="ri-map-pin-line shrink-0" />
            <span className="truncate">{profile.location}</span>
          </div>
        )}
        {profile.business_description && (
          <p className="text-xs text-stone-500 leading-relaxed line-clamp-2">{profile.business_description}</p>
        )}
      </div>

      {/* Tags */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {profile.brand_positioning && (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${posColor}`}>
            {profile.brand_positioning}
          </span>
        )}
        {profile.default_tone && (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-stone-100 text-stone-500 capitalize">
            {profile.default_tone.replace('_', ' ')}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate('/dashboard/generate', { state: { profileId: profile.id } })}
          className="flex-1 flex items-center justify-center gap-1.5 text-xs font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white py-2.5 rounded-xl hover:from-orange-600 hover:to-amber-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-flashlight-fill text-xs" />
          Generate Ads
        </button>
        <button
          onClick={onEdit}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-stone-200 text-stone-500 hover:border-orange-300 hover:text-orange-500 transition-all duration-200 cursor-pointer"
        >
          <i className="ri-edit-line text-sm" />
        </button>
      </div>
    </div>
  );
}

export default function ProfilesPage() {
  const navigate = useNavigate();
  const { profiles, loading, deleteProfile, duplicateProfile } = useBusinessProfiles();
  const { plan, profileLimit } = useUserPlan();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState<string | null>(null);

  const canCreate = profiles.length < profileLimit;
  const planLabel = plan === 'free' ? 'Free' : plan === 'pro' ? 'Pro' : 'Agency';
  const limitLabel = profileLimit < 999 ? `${profiles.length} / ${profileLimit}` : `${profiles.length}`;

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    await deleteProfile(id);
    setDeletingId(null);
  };

  const handleDuplicate = async (profile: BusinessProfile) => {
    if (!canCreate) return;
    setDuplicating(profile.id);
    await duplicateProfile(profile);
    setDuplicating(null);
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <header className="h-16 bg-white border-b border-stone-100 flex items-center justify-between px-6 shrink-0 sticky top-0 z-10">
        <div>
          <h1 className="text-base font-bold text-stone-900">Business Profiles</h1>
          <p className="text-xs text-stone-400">Manage your saved business contexts</p>
        </div>
        <div className="flex items-center gap-3">
          {!canCreate && (
            <Link
              to="/pricing"
              className="flex items-center gap-1.5 text-xs font-semibold text-orange-500 bg-orange-50 hover:bg-orange-100 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-vip-crown-line" />
              Upgrade for more profiles
            </Link>
          )}
          {canCreate && (
            <Link
              to="/dashboard/profiles/new"
              className="flex items-center gap-1.5 text-xs font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-amber-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Add Business Profile
            </Link>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Plan usage banner */}
        <div className="flex items-center gap-3 bg-white border border-stone-100 rounded-xl px-5 py-3 mb-6">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-100 shrink-0">
            <i className="ri-building-2-line text-orange-500 text-sm" />
          </div>
          <div className="flex-1">
            <span className="text-sm font-semibold text-stone-800">{limitLabel} Business Profiles</span>
            <span className="text-xs text-stone-400 ml-2">on {planLabel} plan</span>
          </div>
          {plan === 'free' && (
            <Link to="/pricing" className="text-xs font-semibold text-orange-500 hover:text-orange-600 whitespace-nowrap">
              Upgrade to Pro for 3 profiles →
            </Link>
          )}
          {plan === 'pro' && (
            <Link to="/pricing" className="text-xs font-semibold text-orange-500 hover:text-orange-600 whitespace-nowrap">
              Upgrade to Agency for unlimited →
            </Link>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-stone-100 h-64 animate-pulse" />
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-stone-100 mb-5">
              <i className="ri-building-2-line text-stone-400 text-4xl" />
            </div>
            <h2 className="text-lg font-bold text-stone-800 mb-2">No Business Profiles yet</h2>
            <p className="text-sm text-stone-500 max-w-sm leading-relaxed mb-6">
              Create a Business Profile to store your business info once and reuse it automatically every time you generate ads.
            </p>
            <div className="flex flex-col gap-3 items-center">
              {['Save business info once', 'Auto-fill every ad campaign', 'Supports multiple clients', 'Plan-based profile limits'].map(f => (
                <div key={f} className="flex items-center gap-2 text-sm text-stone-500">
                  <i className="ri-check-line text-orange-400" /> {f}
                </div>
              ))}
            </div>
            <Link
              to="/dashboard/profiles/new"
              className="mt-8 flex items-center gap-2 text-sm font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white px-6 py-3 rounded-xl hover:from-orange-600 hover:to-amber-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Create Your First Business Profile
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {profiles.map(profile => (
                <ProfileCard
                  key={profile.id}
                  profile={profile}
                  onEdit={() => navigate(`/dashboard/profiles/${profile.id}/edit`)}
                  onDuplicate={() => handleDuplicate(profile)}
                  onDelete={() => handleDelete(profile.id)}
                />
              ))}

              {/* Add slot */}
              {canCreate && (
                <Link
                  to="/dashboard/profiles/new"
                  className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-stone-200 hover:border-orange-300 p-8 text-center transition-all duration-200 cursor-pointer group"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-stone-100 group-hover:bg-orange-50 mb-3 transition-colors">
                    <i className="ri-add-line text-stone-400 group-hover:text-orange-400 text-2xl transition-colors" />
                  </div>
                  <p className="text-sm font-semibold text-stone-600 group-hover:text-orange-500 transition-colors">Add Business Profile</p>
                  <p className="text-xs text-stone-400 mt-1">{profileLimit < 999 ? `${profileLimit - profiles.length} slot${profileLimit - profiles.length !== 1 ? 's' : ''} remaining` : 'Unlimited'}</p>
                </Link>
              )}

              {/* Locked slot for free users */}
              {!canCreate && plan === 'free' && (
                <Link
                  to="/pricing"
                  className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-stone-200 hover:border-orange-300 p-8 text-center transition-all duration-200 cursor-pointer group"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-stone-100 group-hover:bg-orange-50 mb-3 transition-colors">
                    <i className="ri-lock-2-line text-stone-400 group-hover:text-orange-400 text-2xl transition-colors" />
                  </div>
                  <p className="text-sm font-semibold text-stone-600 group-hover:text-orange-500 transition-colors">Upgrade to Pro</p>
                  <p className="text-xs text-stone-400 mt-1">Get up to 3 Business Profiles</p>
                </Link>
              )}
            </div>
          </>
        )}
      </div>

      {/* Close dropdown when clicking outside */}
      {profiles.length > 0 && (
        <div
          className="fixed inset-0 z-10"
          style={{ display: 'none' }}
          onClick={() => {}}
        />
      )}
    </div>
  );
}
